<template>
  <div class="step-config-panel">
    <div class="config-header">
      <h4 v-if="!isLocked">请先锁定步骤配置，然后开始操作</h4>
      <h4 v-else>配置已锁定</h4>
      <button
        v-if="!isLocked"
        class="config-button lock"
        :disabled="isConfigInvalid"
        :title="
          isConfigInvalid ? '当前配置不合法，无法锁定' : '锁定配置并开始操作'
        "
        @click="$emit('lock')"
      >
        锁定配置
      </button>
      <button v-else class="config-button modify" @click="$emit('modify')">
        修改配置
      </button>
    </div>

    <div class="config-body" :class="{ 'config-body-fixed': isLocked }">
      <div
        v-for="step in configurableSteps"
        :key="step.title"
        class="config-group"
      >
        <h5 class="config-group-title">{{ step.title }}</h5>

        <div v-if="isLocked" class="config-summary">
          <div class="selected-option">
            <span>{{ getSelectedOption(step)?.label || "未配置" }}</span>
          </div>
        </div>

        <div v-else class="options-grid">
          <div
            v-for="option in step.configOptions"
            :key="option.id"
            class="option-card"
            :class="{
              selected: step.enabled === option.id,
              disabled: option.disabled,
            }"
            :title="option.disabled ? option.disabledReason : ''"
            @click="
              !option.disabled && updateStep(step, { enabled: option.id })
            "
          >
            <div v-if="option.imageUrl" class="option-image-container">
              <img
                :src="option.imageUrl"
                class="option-image"
                alt="option image"
              />
            </div>
            <span class="option-label">{{ option.label }}</span>
            <div class="radio-check">
              <div class="radio-check-inner"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!configurableSteps.length" class="no-options">
        （无可用配置项）
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
const props = defineProps({
  steps: { type: Array, required: true },
  isLocked: { type: Boolean, default: false },
  isConfigInvalid: { type: Boolean, default: false },
});
const emit = defineEmits(["lock", "modify", "update-step"]);

const configurableSteps = computed(() => props.steps.filter((s) => s.optional));
const updateStep = (step, newConfig) => {
  emit("update-step", { stepTitle: step.title, newConfig });
};
const getSelectedOption = (step) => {
  return step.configOptions.find((opt) => opt.id === step.enabled);
};
</script>

<style scoped>
.step-config-panel {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: var(--border-radius-base);
  padding: 0.5rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}
.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.config-header h4 {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1.25rem;
}
.config-button {
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-small);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}
.config-button.lock:disabled {
  background-color: var(--color-primary-disabled);
  cursor: not-allowed;
}
.config-button.lock {
  background-color: var(--color-primary);
  color: white;
}
.config-button.lock:hover:not(:disabled) {
  background-color: var(--color-primary-light);
}
.config-button.modify {
  background-color: #f56c6c;
  color: white;
}
.config-button.modify:hover {
  background-color: var(--color-danger-light);
}
.config-body {
  margin-top: 1rem;
}
.config-body-fixed {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
}
.no-options {
  font-size: 0.9rem;
  color: var(--color-text-placeholder);
}
.config-group {
  margin-bottom: 1rem;
}
.config-group-title {
  margin: 0 0 0.8rem 0;
  font-size: 1.1rem;
  color: var(--color-text-primary);
  font-weight: 600;
}
/* 布局优化：修改网格布局，使第一列（不启用）变窄 */
.options-grid {
  display: grid;
  grid-template-columns: 120px 1fr 1fr;
  gap: 1rem;
}
.option-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* 从顶部开始排列 */
  padding: 1rem 0.5rem;
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-base);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: #f9fafb;
  min-height: 25vh; /* 增大卡片高度 */
}
.option-card:hover:not(.disabled) {
  border-color: var(--color-primary-light);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.option-card.selected {
  border-color: var(--color-primary);
  background-color: #f0f8ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}
.option-card.disabled {
  cursor: not-allowed;
  background-color: #f5f7fa;
  opacity: 0.6;
}
.option-card.disabled .option-label {
  color: #a8abb2;
}
/* 布局优化：让图片容器占据主要空间 */
.option-image-container {
  width: 100%;
  flex-grow: 1; /* 占据所有可用垂直空间 */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.8rem;
  min-height: 0; /* flexbox hack for overflow */
}
.option-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.option-label {
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  flex-shrink: 0; /* 防止标签被压缩 */
}
.radio-check {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--color-border);
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.option-card.selected .radio-check {
  border-color: var(--color-primary);
}
.radio-check-inner {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background-color: var(--color-primary);
  transform: scale(0);
  transition: transform 0.2s ease;
}
.option-card.selected .radio-check-inner {
  transform: scale(1);
}
.config-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.selected-option {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.8rem;
  background-color: #f4f4f5;
  border-radius: var(--border-radius-small);
  font-size: 1rem;
  color: var(--color-text-secondary);
}
</style>
